import { useMutation, useQuery } from "@tanstack/react-query";
import axios from "const/axios";

const emailBase = (invoiceId) => `/invoice/${invoiceId}/email`;

/**
 * Who the invoice is billed to and the address on file for them. Fetched when the send dialog
 * opens rather than carried on the list row, so a client whose email was filled in elsewhere is
 * picked up without reloading the page.
 */
export const useInvoiceEmailRecipient = (invoiceId, options = {}) =>
  useQuery({
    queryKey: ["invoiceEmailRecipient", invoiceId],
    queryFn: async () => (await axios.get(`${emailBase(invoiceId)}/recipient`)).data,
    enabled: Boolean(invoiceId) && options.enabled !== false,
    staleTime: 0,
    ...options,
  });

export const useSendInvoiceEmail = (options = {}) =>
  useMutation({
    mutationFn: async ({
      invoiceId,
      to,
      cc,
      bcc,
      fromEmail,
      subject,
      message,
      saveRecipientEmail,
    }) =>
      (
        await axios.post(emailBase(invoiceId), {
          to,
          cc,
          bcc,
          fromEmail,
          subject,
          message,
          saveRecipientEmail,
        })
      ).data,
    onSuccess: options.onSuccess,
    onError: options.onError,
  });

/**
 * Send status + history for one invoice (who sent what to whom, when).
 * Backed by email_send_log filtered on the Invoice:<id> tag, so it matches
 * what the Client Emails tab and dashboard Emails view show.
 */
const emptyHistory = (size) => ({
  content: [],
  totalItems: 0,
  totalPages: 0,
  currentPage: 0,
  pageSize: size,
});

export const fetchInvoiceEmailHistory = async (invoiceId, page = 0, size = 20) => {
  const response = await axios.get(`${emailBase(invoiceId)}/history`, {
    params: { page, size },
  });
  // This controller returns the page bare (like its recipient/send endpoints),
  // not wrapped in GenericResponse the way /email-logs is.
  const payload = response?.data?.data ?? response?.data;
  return Array.isArray(payload?.content) ? payload : emptyHistory(size);
};

export const useInvoiceEmailHistory = (invoiceId, page = 0, size = 20, options = {}) =>
  useQuery({
    queryKey: ["invoiceEmailHistory", invoiceId, page, size],
    queryFn: () => fetchInvoiceEmailHistory(invoiceId, page, size),
    enabled: Boolean(invoiceId) && options.enabled !== false,
    staleTime: 0,
    ...options,
  });
